/**
 * <domain-guard>
 * ---------------------------------------------------------------------------
 * Sends visitors to the canonical storefront domain when the page is being
 * served from a hostname we don't own - typically a reverse proxy that fetches
 * the real store and re-serves it under its own domain.
 *
 * Configuration arrives as base64-encoded data attributes from
 * snippets/domain-guard.liquid. See that file for why they are encoded.
 *
 * Loading: this file is referenced in <head> WITHOUT defer or async, so the
 * element is defined before <body> is parsed. Because <domain-guard> is the
 * first element in <body>, it upgrades and runs during parsing, before any
 * content paints. The file is deliberately tiny to keep that cost negligible.
 *
 * Design note: the guard FAILS OPEN. Any decode failure, missing config, or
 * unexpected state results in no redirect. A broken deploy must never be able
 * to lock customers out of the real store.
 *
 * No shadow DOM: markup lives in the Liquid snippet so the theme's own styles
 * apply normally.
 */

/** Hostnames used during local theme development. */
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0', '[::1]'];

/**
 * Hostname suffixes that always belong to Shopify. Kept separate from the
 * Liquid-supplied allowlist because these are platform-wide, not store
 * specific (preview links, the theme editor, the permanent domain).
 */
const TRUSTED_SUFFIXES = ['.myshopify.com', '.shopifypreview.com', '.shopify.com'];

/**
 * How long to wait before assuming the redirect was blocked and showing the
 * fallback notice. A real navigation tears down this document well before it
 * fires, so in the normal case the callback never runs.
 */
const REDIRECT_GRACE_MS = 1200;

/**
 * Decode a base64 data attribute into a normalised hostname string.
 * Returns an empty string on any failure so callers can fail open.
 *
 * @param {string|undefined} value
 * @returns {string}
 */
function decodeValue(value) {
  if (!value) return '';

  try {
    // atob is used rather than TextDecoder: hostnames are ASCII, and atob is
    // available everywhere without a polyfill or an async boundary.
    return atob(value).trim().toLowerCase();
  } catch (error) {
    return '';
  }
}

/**
 * Decode a base64 comma-separated list into an array of hostnames.
 *
 * @param {string|undefined} value
 * @returns {string[]}
 */
function decodeList(value) {
  const decoded = decodeValue(value);
  if (!decoded) return [];

  return decoded
    .split(',')
    .map((host) => host.trim())
    .filter(Boolean);
}

class DomainGuard extends HTMLElement {
  connectedCallback() {
    // The theme editor renders the storefront in a frame it controls.
    // Redirecting would break editing, so stand down.
    if (this.dataset.designMode === 'true') return;

    const canonicalHost = decodeValue(this.dataset.canonicalHost);

    // No usable config: fail open rather than guessing.
    if (!canonicalHost) return;

    const allowedHosts = decodeList(this.dataset.allowedHosts);
    const currentHost = window.location.hostname.toLowerCase();

    if (DomainGuard.isTrustedHost(currentHost, allowedHosts)) return;

    this.redirectToCanonical(canonicalHost);
  }

  /**
   * Decide whether the current hostname is one of ours.
   *
   * @param {string} host - the hostname currently in the address bar
   * @param {string[]} allowedHosts - hostnames supplied by Liquid
   * @returns {boolean}
   */
  static isTrustedHost(host, allowedHosts) {
    // An empty hostname means a non-http context (file://, about:blank).
    // Nothing to protect, so treat it as trusted.
    if (!host) return true;

    if (LOCAL_HOSTS.includes(host)) return true;
    if (allowedHosts.includes(host)) return true;

    return TRUSTED_SUFFIXES.some((suffix) => host.endsWith(suffix));
  }

  /**
   * Rebuild the current URL against the canonical host, preserving path,
   * query and hash so the visitor lands on the page they asked for.
   *
   * @param {string} canonicalHost
   */
  redirectToCanonical(canonicalHost) {
    let target;

    try {
      target = new URL(window.location.href);
      target.protocol = 'https:';
      target.hostname = canonicalHost;
      target.port = ''; // drop any port carried over from the spoofed origin
    } catch (error) {
      return; // fail open
    }

    // Guard against a redirect loop if we are somehow already canonical.
    if (target.href === window.location.href) return;

    // replace() rather than assign() so the spoofed URL is not left in the
    // session history for the back button to return to.
    window.location.replace(target.href);

    // If the navigation is blocked, this document survives - surface the
    // notice so the visitor is warned and given a working link.
    window.setTimeout(() => {
      this.revealNotice(canonicalHost, target.href);
    }, REDIRECT_GRACE_MS);
  }

  /**
   * Populate and reveal the fallback notice.
   *
   * The hostname and href are written here rather than in Liquid so the
   * canonical domain never appears as a literal in the markup.
   *
   * @param {string} canonicalHost
   * @param {string} canonicalHref
   */
  revealNotice(canonicalHost, canonicalHref) {
    const notice = this.querySelector('[data-domain-guard-notice]');
    if (!notice) return;

    const link = notice.querySelector('[data-domain-guard-link]');
    const hostSlot = notice.querySelector('[data-domain-guard-host]');

    if (link) link.href = canonicalHref;
    if (hostSlot) hostSlot.textContent = canonicalHost;

    // role="alert" is already on the notice, so revealing it announces the
    // message to assistive technology without stealing focus.
    notice.hidden = false;
  }
}

// Guard the definition: the asset could be included twice by a section or app.
if (!customElements.get('domain-guard')) {
  customElements.define('domain-guard', DomainGuard);
}
